import { IJournalImportResult } from './journal-import.model';

/**
 * Accounto journal import wire shape (POST /api/integrations/accounto/import).
 *
 * The RESULT shape is shared with D365 BC / Abacus / Topal via `IJournalImportResult`
 * (`./journal-import.model`). Accounto has no fiscal-year endpoint: the FE pulls `fiscalYear*` (and
 * optional `previousFiscalYear*`) from the active Annual Report's General Info, and the BE reads the
 * postings and bookings of exactly that range (REV-21119 D6, Abacus pattern).
 */
export interface IAccountoImportBody {
    companyId: string;
    connectionId: string;
    /** Accounto tenant id, sourced from the connection mapping. */
    externalCompanyId: string;
    /** ISO YYYY-MM-DD. */
    fiscalYearStart: string;
    fiscalYearEnd: string;
    /** Optional YoY pair — both halves or neither (the BE rejects a partial pair). */
    previousFiscalYearStart?: string;
    previousFiscalYearEnd?: string;
}

/**
 * The report an Accounto import was started for. Kept in the store next to the run, never sent to the BE.
 * The import step uses it to take a result only for the report on screen (frontend guidelines §4:
 * late results check where the user is now).
 */
export interface IAccountoImportTarget {
    companyId: string;
    annualReportId: string;
}

/**
 * The result of the Accounto import slice, but only when it succeeded FOR this report.
 *
 * The slice is global and the import effect outlives the import step, so a success can land (or still sit
 * in the store) after the user opened another report. That report gets `null`, as does a success whose
 * target was cleared by a reset while the run was in flight.
 */
export function accountoImportResultForReport(
    slice: {
        importStatus: 'idle' | 'loading' | 'success' | 'error';
        importResult: IJournalImportResult | null;
        importFor: IAccountoImportTarget | null;
    },
    report: { companyId: string | null | undefined; annualReportId: string | null | undefined },
): IJournalImportResult | null {
    if (slice.importStatus !== 'success' || slice.importResult == null || slice.importFor == null) return null;
    if (report.companyId == null || report.annualReportId == null) return null;
    const matches =
        slice.importFor.companyId === report.companyId && slice.importFor.annualReportId === report.annualReportId;
    return matches ? slice.importResult : null;
}
